import React from 'react';
import { Group, Text, Menu, Avatar, Badge, ActionIcon, Box, UnstyledButton, Image, Burger } from '@mantine/core';
import {
  IconLogout,
  IconChevronDown,
  IconBell,
  IconKey,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../application/store/authStore';
import { getUserRoleLabel } from '../../../domain/value-objects/UserRole';
import { MINEMINER_ROUTES, buildRoute, ELKO_EVENT_ID } from '../../routing/routes';
import LogoSvg from '../../../assets/images/logo.svg';

// Temporary tradeshow landing (epic-019, sprint-123). For booth immediacy the brand
// logo + wordmark route to the active show's event page rather than a dashboard. This
// only ships in the carved `Frontend-MineMiner/` bundle (carved-prod / mymineminer.com);
// core MentionedMe has its own Header and is unaffected.
// TODO(post-Elko): revert to the standard MineMiner home/dashboard route — sibling to
// the post-login redirect revert, TECH_DEBT #127. ELKO_EVENT_ID now shared from routes.ts
// (sprint-124).
const BRAND_HOME_ROUTE = buildRoute(MINEMINER_ROUTES.EVENT_DETAIL, { eventId: ELKO_EVENT_ID });

export interface HeaderProps {
  /**
   * Callback fired when the hamburger menu is clicked (mobile only).
   * Used to toggle the mobile navigation drawer.
   */
  onMenuClick?: () => void;
}

/**
 * Header — MineMiner SPA top bar.
 *
 * sprint-104 (epic-013 Sprint B, step 2d / S5). Forked from `Frontend/`'s
 * Header and slimmed:
 *
 *   - Brand wordmark is always "MineMiner" (no MentionedMe / Sync swap).
 *
 * sprint-108 S2 — user menu now has "Change password" as well as Logout
 * (entry point for the auth-gated /MineMiner/change-password route).
 * Profile / Settings still link into the core SPA's CustomerLayout /
 * AdminLayout which don't exist here.
 */
export const Header: React.FC<HeaderProps> = ({ onMenuClick }): React.ReactElement => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);

  const handleLogout = (): void => {
    void logout().then(() => {
      navigate('/login');
    });
  };

  const handleChangePassword = (): void => {
    navigate(MINEMINER_ROUTES.CHANGE_PASSWORD);
  };

  const getUserInitials = (): string => {
    if (user?.displayName) {
      const names = user.displayName.split(' ');
      if (names.length >= 2 && names[0] && names[1]) {
        return `${names[0][0]}${names[1][0]}`.toUpperCase();
      }
      return user.displayName.substring(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return '??';
  };

  return (
    <Group h="100%" px="md" justify="space-between" wrap="nowrap">
      <Group gap="xs">
        {onMenuClick && (
          <Burger
            opened={false}
            onClick={onMenuClick}
            hiddenFrom="sm"
            size="sm"
            aria-label="Toggle navigation"
          />
        )}

        <UnstyledButton
          onClick={(): void => navigate(BRAND_HOME_ROUTE)}
          aria-label="MineMiner home"
        >
          <Group gap="xs" wrap="nowrap">
            <Image src={LogoSvg} alt="MineMiner Logo" h={32} w="auto" />
            <Text size="xl" fw={700} ff="Comfortaa, sans-serif" c="brandPrimary">
              MineMiner
            </Text>
          </Group>
        </UnstyledButton>
      </Group>

      <Group gap="md">
        <ActionIcon variant="subtle" color="gray" size="lg" aria-label="Notifications">
          <IconBell size={20} />
        </ActionIcon>

        <Menu shadow="md" width={200} position="bottom-end">
          <Menu.Target>
            <UnstyledButton>
              <Group gap="sm">
                <Avatar color="blue" radius="xl">
                  {getUserInitials()}
                </Avatar>
                {/* Below `sm` the name + role badge are hidden so the 60px header
                    stays single-line and never wraps into the breadcrumb beneath it
                    (sprint-115 SESSION_006 fold-in). The avatar + chevron still open
                    the same account menu. `sm` matches the burger's hiddenFrom boundary. */}
                <Box visibleFrom="sm">
                  <Text size="sm" fw={500}>
                    {user?.displayName ?? user?.email}
                  </Text>
                  {/* sprint-217: `role` became nullable when the MineMinerRep fallback was
                      removed from the auth store. Rendering the badge unconditionally would
                      leave an empty blue pill for a user holding no recognised role. */}
                  {user?.role && (
                    <Badge size="xs" color="blue" variant="light">
                      {getUserRoleLabel(user.role)}
                    </Badge>
                  )}
                </Box>
                <IconChevronDown size={14} />
              </Group>
            </UnstyledButton>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Label>Account</Menu.Label>
            <Menu.Item leftSection={<IconKey size={14} />} onClick={handleChangePassword}>
              Change password
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item color="red" leftSection={<IconLogout size={14} />} onClick={handleLogout}>
              Logout
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Group>
  );
};
